/**
 * Normalised API errors.
 *
 * CLAUDE.md Part II §13 — every refusal surfaces a `decision_ref`, copyable in
 * one click. The client parses it out of the response so no screen can swallow
 * it by forgetting to look.
 */

/** The closed set of authorization outcomes. Invariant 8: there is no `freeze`. */
export const AUTHZ_OUTCOMES = ['allow', 'review', 'step_up', 'refuse_to_submit'] as const
export type AuthzOutcome = (typeof AUTHZ_OUTCOMES)[number]

export function isAuthzOutcome(value: unknown): value is AuthzOutcome {
  return typeof value === 'string' && (AUTHZ_OUTCOMES as readonly string[]).includes(value)
}

export class ApiError extends Error {
  readonly status: number
  /** Stable machine-readable code from the backend, when it supplies one. */
  readonly code: string | null
  /**
   * The authorization decision reference. Present on every refusal.
   * Screens MUST surface this — it is how an operator answers "why was I declined?".
   */
  readonly decisionRef: string | null
  /**
   * What the caller would have needed, as the backend named it — `"ops"`,
   * `"finance or growth"`.
   *
   * Parsed here rather than inferred by a screen, because the backend sends it
   * for exactly that reason: `adminError.RequiredRole` is populated on every
   * RBAC refusal (`internal/server/admin.go`) so the console does not have to
   * hold a second copy of the permission model. Dropping it on the floor was
   * worse than not having it — the denied panel went on to state that the role
   * was "not stated by the backend", which is a claim about a payload nobody
   * read, on the one screen an operator reads to find out who to ask.
   *
   * `null` means the refusal genuinely carried none: an authz refusal is about
   * risk rather than role scope and names a `decisionRef` instead.
   */
  readonly requiredRole: string | null
  readonly outcome: AuthzOutcome | null
  readonly requestId: string | null

  constructor(init: {
    message: string
    status: number
    code?: string | null
    decisionRef?: string | null
    requiredRole?: string | null
    outcome?: AuthzOutcome | null
    requestId?: string | null
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code ?? null
    this.decisionRef = init.decisionRef ?? null
    this.requiredRole = init.requiredRole ?? null
    this.outcome = init.outcome ?? null
    this.requestId = init.requestId ?? null
  }

  /** True when the operator's role is the problem, so the UI can name the required role. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /**
   * True when the backend refused the instruction on risk grounds rather than
   * failing. A refusal is a valid answer and renders as one — never as a crash.
   */
  get isRefusal(): boolean {
    return this.outcome === 'refuse_to_submit' || this.decisionRef !== null
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The console could not reach the wallet API.')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

function pickString(source: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return null
}

/** Builds an ApiError from a non-2xx response body, tolerating shape drift. */
export function toApiError(status: number, body: unknown, requestId: string | null): ApiError {
  if (typeof body !== 'object' || body === null) {
    return new ApiError({ message: `Request failed with status ${status}.`, status, requestId })
  }

  const raw = body as Record<string, unknown>
  const nested = typeof raw['error'] === 'object' && raw['error'] !== null
    ? (raw['error'] as Record<string, unknown>)
    : raw

  const outcomeValue = pickString(nested, 'outcome')

  return new ApiError({
    message:
      pickString(nested, 'message', 'detail', 'title') ?? `Request failed with status ${status}.`,
    status,
    code: pickString(nested, 'code', 'reason', 'reason_code'),
    decisionRef: pickString(nested, 'decision_ref', 'decisionRef'),
    requiredRole: pickString(nested, 'required_role', 'requiredRole'),
    outcome: isAuthzOutcome(outcomeValue) ? outcomeValue : null,
    requestId,
  })
}
